import { getContentfulGqlClient } from "@/contentful/wac-content-gql-client.ts";

import { mapTemplateItem } from "../map-template-item";

import { allProductTemplatesQuery } from "./gql.ts";

import type {
    AllProductTemplatesData,
    AllProductTemplatesQueryVariables,
    ContentfulGalleryDataSource,
    ContentfulGalleryDataType,
    ContentfulGalleryPage,
    ContentfulGalleryPageArgs,
    GqlTemplate
} from "./types.ts";

const GQL_FETCH_LIMIT = 100;

export interface GetContentfulGalleryItemsArgs {
    contentType: ContentfulGalleryDataType;
    loaded: number;
}

const contentfulGalleryDataSources: Record<
    ContentfulGalleryDataType,
    ContentfulGalleryDataSource
> = {
    productTemplate: {
        fetchPage: fetchProductTemplatePage,
        mapItem: (item) => mapTemplateItem(item as GqlTemplate)
    }
};

export const getContentfulGalleryItems = async ({
    contentType,
    loaded
}: GetContentfulGalleryItemsArgs): Promise<unknown[]> => {
    const dataSource = contentfulGalleryDataSources[contentType];
    const cacheKey = getCacheKey({ contentType, loaded });
    const cached = getWindowCache(cacheKey);
    if (cached) {
        return cached;
    }

    const firstPage = await dataSource.fetchPage({
        limit: GQL_FETCH_LIMIT,
        skip: loaded
    });
    const allItems = firstPage.items.map(dataSource.mapItem);

    const remainingSkips = getRemainingSkips({
        loaded,
        total: firstPage.total,
        limit: GQL_FETCH_LIMIT
    });
    const remainingPages = await Promise.all(
        remainingSkips.map((skip) =>
            dataSource.fetchPage({
                limit: GQL_FETCH_LIMIT,
                skip
            })
        )
    );

    for (const page of remainingPages) {
        allItems.push(...page.items.map(dataSource.mapItem));
    }

    setWindowCache(cacheKey, allItems);
    return allItems;
};

async function fetchProductTemplatePage({
    limit,
    skip
}: ContentfulGalleryPageArgs): Promise<ContentfulGalleryPage> {
    const result = await getContentfulGqlClient()
        .query<AllProductTemplatesData, AllProductTemplatesQueryVariables>(
            allProductTemplatesQuery,
            { limit, skip }
        )
        .toPromise();

    const collection = result.data?.productTemplateCollection;
    return {
        total: collection?.total ?? 0,
        items: collection?.items ?? []
    };
}

function getRemainingSkips({
    loaded,
    total,
    limit
}: {
    loaded: number;
    total: number;
    limit: number;
}): number[] {
    const skips: number[] = [];
    for (let skip = loaded + limit; skip < total; skip += limit) {
        skips.push(skip);
    }
    return skips;
}

function getCacheKey({
    contentType,
    loaded
}: {
    contentType: ContentfulGalleryDataType;
    loaded: number;
}): string {
    return `__UTG_${contentType.toUpperCase()}_FROM_${loaded}__`;
}

function getWindowCache(key: string): unknown[] | null {
    const cached = (window as unknown as Record<string, unknown>)[key];
    return Array.isArray(cached) ? (cached as unknown[]) : null;
}

function setWindowCache(key: string, items: unknown[]): void {
    (window as unknown as Record<string, unknown>)[key] = items;
}
